'use client';
// Upon refactoring, consider https://cloudinary.com/blog/guest_post/e-commerce-product-variant-customization-in-next-js

import { type ColorPicker } from '@/sanity/schemaTypes/singletons/colorPickerType';
import {
  Radio,
  RadioGroup,
  Switch,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Button,
  Textarea
} from '@headlessui/react';
import {
  Bars3BottomLeftIcon,
  Bars3BottomRightIcon,
  Bars3Icon,
  BarsArrowDownIcon,
  BarsArrowUpIcon,
  ChevronDownIcon,
  PlusIcon
} from '@heroicons/react/20/solid';
import { MinusCircleIcon, PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useActionState, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useCart } from '@/components/cart/cart-context';
import { addItem } from '@/components/cart/actions';
import { formatPrice } from '@/lib/utils';
import { fonts, fontClassNames } from '@/lib/CustomFonts';
import dynamic from 'next/dynamic';
const CustomTextCanvas = dynamic(() => import('@/components/CustomTextCanvas'), {
  ssr: false
});
import { type CustomTextTranslations } from '@/sanity/schemaTypes/objects/translations/customTextTranslationsType';
import { type ProductPageTranslations } from '@/sanity/schemaTypes/objects/translations/productPageTranslationsType';

type Props = {
  data: {
    customTextProduct: {
      gid: string;
      variants: {
        _type: 'productVariant';
        title: string;
        price: number;
        gid: string;
      }[];
    };
    settings: {
      productPageTranslations: ProductPageTranslations;
      customTextTranslations: CustomTextTranslations;
    };
    colors: ColorPicker;
  };
  locale: string;
};

type TextLine = {
  text: string;
  font: {
    name: string;
    font: string;
    className: string;
  };
  fontSize: number;
  align: 'left' | 'center' | 'right';
  verticalAlign: 'top' | 'middle' | 'bottom';
};

const emptyLine = (defaultTextTranslation: string): TextLine => ({
  text: defaultTextTranslation,
  font: fonts[0] || { name: 'default', font: 'sans-serif', className: '' },
  fontSize: 80,
  align: 'center',
  verticalAlign: 'middle'
});

export default function CustomText({ data, locale }: Props) {
  const [textLines, _setTextLines]: [TextLine[], any] = useState([
    { ...emptyLine(data.settings.customTextTranslations.defaultTextTranslation) }
  ]);
  const [selectedColor, setSelectedColor] = useState(data.colors.colorCategories[0]?.colors[0]);
  const [orientationInverted, setOrientationInverted] = useState(false);
  const [selectedDimension, setSelectedDimension] = useState<'width' | 'height'>('width');
  const [stickerWidth, setStickerWidth] = useState(100);
  const [stickerHeight, setStickerHeight] = useState(0);

  const [getSvgFunction, setGetSvgFunction] = useState<() => string>(() => () => '');
  const [selectedVariant, setSelectedVariant] = useState(data.customTextProduct.variants[0]);
  const { addCartItem } = useCart();
  const [, formAction, addItemPending] = useActionState(addItem, null);

  const setTextLines = (newTextLines: TextLine[]) => {
    _setTextLines(newTextLines);
  };

  useEffect(() => {
    const stickerSurface = (stickerWidth + 5 * stickerHeight + 5) / 100;
    const charactersCount = textLines.reduce((acc, line) => acc + line.text.length, 0);
    const cost = stickerSurface * 5.37 + stickerSurface * 2.27 + charactersCount * 0.25;
    const closestVariant = data.customTextProduct.variants.reduce((prev, curr) => {
      return Math.abs(curr.price - cost) < Math.abs(prev.price - cost) ? curr : prev;
    });

    setSelectedVariant(closestVariant);
  }, [stickerWidth, stickerHeight, textLines, data.customTextProduct.variants]);

  return (
    <div
      className={`mx-auto max-w-2xl px-4 pt-6 pb-16 sm:px-6 sm:pb-24 lg:max-w-7xl lg:px-8 ${fontClassNames}`}
    >
      {/* Preview */}
      <CustomTextCanvas
        width={stickerWidth}
        height={stickerHeight}
        mainDimension={selectedDimension}
        color={selectedColor?.colorCode || '#000'}
        inverted={orientationInverted}
        texts={textLines}
        updateTexts={setTextLines}
        setStickerWidth={setStickerWidth}
        setStickerHeight={setStickerHeight}
        setGetSvgFunction={setGetSvgFunction}
      />

      <form
        action={async () => {
          const attributes = [];
          if (selectedColor) {
            attributes.push({
              key: data.settings.productPageTranslations.colorLabelTranslation,
              value: selectedColor.title
            });
          }
          if (orientationInverted) {
            attributes.push({
              key: data.settings.productPageTranslations.orientationLabelTranslation,
              value: data.settings.productPageTranslations.invertedTranslation
            });
          }
          attributes.push({
            key: data.settings.productPageTranslations.sizeLabelTranslation,
            value: `${stickerHeight} x ${stickerWidth} cm`
          });
          attributes.push({
            key: data.settings.customTextTranslations.textLabelTranslation,
            value: textLines.map((line) => line.text).join(' ')
          });
          attributes.push({
            key: 'data-custom-text',
            value: getSvgFunction()
          });
          addCartItem(
            {
              title: selectedVariant?.title,
              id: data.customTextProduct.gid,
              price: selectedVariant?.price
            } as any,
            data.customTextProduct as any,
            attributes
          );
          selectedVariant?.gid &&
            (await formAction({
              selectedVariantId: selectedVariant?.gid,
              attributes
            }));
        }}
      >
        {/* Text lines input */}
        <div className="mb-8 flex flex-col items-start gap-4">
          {textLines.map((line, i) => (
            <div
              key={`${i}-${line.font.name}-${line.fontSize}-${line.align}`}
              className={twMerge(
                'flex w-full flex-wrap items-stretch gap-3 rounded p-2',
                i % 2 === 0 ? 'bg-gray-100' : ''
              )}
            >
              {/* Texte */}
              <div className="grow">
                <label htmlFor={`text-${i}`} className="block text-sm/6 font-medium text-gray-900">
                  {data.settings.customTextTranslations.textLabelTranslation}
                </label>
                <div className="mt-2">
                  <Textarea
                    rows={2}
                    value={line.text}
                    onChange={(e) => {
                      const newTextLines = [...textLines];
                      if (newTextLines[i]) {
                        newTextLines[i].text = e.target.value;
                      }
                      setTextLines(newTextLines);
                    }}
                    id={`text-${i}`}
                    name={`text-${i}`}
                    placeholder={data.settings.customTextTranslations.textPlaceholderTranslation}
                    className="focus:outline-secondary block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
                  />
                </div>
              </div>

              {/* Police */}
              <div>
                <label htmlFor={`font-${i}`} className="block text-sm/6 font-medium text-gray-900">
                  {data.settings.customTextTranslations.fontLabelTranslation}
                </label>
                <div className="relative mt-2">
                  <ChevronDownIcon
                    aria-hidden="true"
                    className="absolute inset-y-0 right-3 size-5 h-full text-gray-400"
                  />
                  <Listbox
                    defaultValue={line.font}
                    onChange={(e) => {
                      const newTextLines = [...textLines];
                      if (newTextLines[i]) {
                        newTextLines[i].font = e;
                      }
                      setTextLines(newTextLines);
                    }}
                  >
                    <ListboxButton
                      className="focus:outline-secondary block w-full appearance-none rounded-md bg-white px-3 py-1.5 text-left text-xl text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
                      style={{ fontFamily: line.font.font }}
                    >
                      {line.font.name}
                    </ListboxButton>
                    <ListboxOptions anchor="bottom" className="z-50">
                      {fonts.map((font, i) => (
                        <ListboxOption
                          key={i}
                          value={font}
                          className="data-[focus]:bg-secondary focus:outline-secondary block w-full appearance-none border-r-[1px] border-b-[1px] border-l-[1px] border-gray-300 bg-white px-3 py-1.5 text-xl text-gray-900 first:rounded-t-md first:border-t-[1px] last:rounded-b-md focus:outline-2 focus:-outline-offset-2 data-[focus]:text-white sm:text-sm/6"
                          style={{ fontFamily: font.font }}
                        >
                          {font.name}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Listbox>
                </div>
              </div>

              {/* Taille */}
              <div>
                <label htmlFor={`size-${i}`} className="block text-sm/6 font-medium text-gray-900">
                  {data.settings.customTextTranslations.fontSizeLabelTranslation}
                </label>
                <div className="mt-2">
                  <Listbox
                    defaultValue={line.fontSize}
                    onChange={(e) => {
                      const newTextLines = [...textLines];
                      if (newTextLines[i]) {
                        newTextLines[i].fontSize = e;
                      }
                      setTextLines(newTextLines);
                    }}
                  >
                    <ListboxButton className="focus:outline-secondary flex w-full appearance-none items-center justify-between rounded-md bg-white px-3 py-1.5 text-center text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6">
                      <MinusCircleIcon
                        className="size-5"
                        aria-hidden="true"
                        onClick={() => {
                          const newTextLines = [...textLines];
                          if (newTextLines[i]) {
                            newTextLines[i].fontSize = Math.max(20, line.fontSize - 1);
                          }
                          setTextLines(newTextLines);
                        }}
                      />
                      {line.fontSize}
                      <PlusCircleIcon
                        className="size-5"
                        aria-hidden="true"
                        onClick={() => {
                          const newTextLines = [...textLines];
                          if (newTextLines[i]) {
                            newTextLines[i].fontSize = Math.min(200, line.fontSize + 1);
                          }
                          setTextLines(newTextLines);
                        }}
                      />
                    </ListboxButton>
                    <ListboxOptions anchor="bottom" className="z-50">
                      {[...Array(181).keys()].map((size) => (
                        <ListboxOption
                          key={size}
                          value={size + 20}
                          className="data-[focus]:bg-secondary focus:outline-secondary block w-full appearance-none border-r-[1px] border-b-[1px] border-l-[1px] border-gray-300 bg-white px-3 py-1.5 text-xl text-gray-900 first:rounded-t-md first:border-t-[1px] last:rounded-b-md focus:outline-2 focus:-outline-offset-2 data-[focus]:text-white sm:text-sm/6"
                        >
                          {size + 20}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Listbox>
                </div>
              </div>

              {/* Alignement */}
              <div className="flex flex-col">
                <div>
                  <label
                    htmlFor={`align-${i}`}
                    className="block text-sm/6 font-medium text-gray-900"
                  >
                    {data.settings.customTextTranslations.textAlignLabelTranslation}
                  </label>
                  <RadioGroup
                    name={`align-${i}`}
                    value={line.align}
                    onChange={(e) => {
                      const newTextLines = [...textLines];
                      if (newTextLines[i]) {
                        newTextLines[i].align = e;
                      }
                      setTextLines(newTextLines);
                    }}
                    className="mt-2 flex flex-wrap items-center gap-3"
                  >
                    <Radio
                      value="left"
                      aria-label={data.settings.customTextTranslations.textAlignLeftTranslation}
                      className="ring-secondary data-checked:bg-secondary relative flex cursor-pointer items-center justify-center rounded p-0.5 focus:outline-hidden data-checked:text-white data-checked:ring-2 data-focus:data-checked:ring-3 data-focus:data-checked:ring-offset-3"
                    >
                      <Bars3BottomLeftIcon className="size-5" aria-hidden="true" />
                      <span className="sr-only">
                        {data.settings.customTextTranslations.textAlignLeftTranslation}
                      </span>
                    </Radio>
                    <Radio
                      value="center"
                      aria-label={data.settings.customTextTranslations.textAlignCenterTranslation}
                      className="ring-secondary data-checked:bg-secondary relative flex cursor-pointer items-center justify-center rounded p-0.5 focus:outline-hidden data-checked:text-white data-checked:ring-2 data-focus:data-checked:ring-3 data-focus:data-checked:ring-offset-3"
                    >
                      <Bars3Icon className="size-5" aria-hidden="true" />
                      <span className="sr-only">
                        {data.settings.customTextTranslations.textAlignCenterTranslation}
                      </span>
                    </Radio>
                    <Radio
                      value="right"
                      aria-label={data.settings.customTextTranslations.textAlignRightTranslation}
                      className="ring-secondary data-checked:bg-secondary relative flex cursor-pointer items-center justify-center rounded p-0.5 focus:outline-hidden data-checked:text-white data-checked:ring-2 data-focus:data-checked:ring-3 data-focus:data-checked:ring-offset-3"
                    >
                      <Bars3BottomRightIcon className="size-5" aria-hidden="true" />
                      <span className="sr-only">
                        {data.settings.customTextTranslations.textAlignRightTranslation}
                      </span>
                    </Radio>
                  </RadioGroup>
                </div>
                <div>
                  <label
                    htmlFor={`vertical-align-${i}`}
                    className="sr-only block text-sm/6 font-medium text-gray-900"
                  >
                    {data.settings.customTextTranslations.verticalAlignLabelTranslation}
                  </label>
                  <RadioGroup
                    name={`vertical-align-${i}`}
                    value={line.verticalAlign}
                    onChange={(e) => {
                      const newTextLines = [...textLines];
                      if (newTextLines[i]) {
                        newTextLines[i].verticalAlign = e;
                      }
                      setTextLines(newTextLines);
                    }}
                    className="mt-2 flex flex-wrap items-center gap-3"
                  >
                    <Radio
                      value="top"
                      aria-label={data.settings.customTextTranslations.verticalAlignTopTranslation}
                      className="ring-secondary data-checked:bg-secondary relative flex cursor-pointer items-center justify-center rounded p-0.5 focus:outline-hidden data-checked:text-white data-checked:ring-2 data-focus:data-checked:ring-3 data-focus:data-checked:ring-offset-3"
                    >
                      <BarsArrowUpIcon className="size-5" aria-hidden="true" />
                      <span className="sr-only">
                        {data.settings.customTextTranslations.verticalAlignTopTranslation}
                      </span>
                    </Radio>
                    <Radio
                      value="middle"
                      aria-label={
                        data.settings.customTextTranslations.verticalAlignMiddleTranslation
                      }
                      className="ring-secondary data-checked:bg-secondary relative flex cursor-pointer items-center justify-center rounded p-0.5 focus:outline-hidden data-checked:text-white data-checked:ring-2 data-focus:data-checked:ring-3 data-focus:data-checked:ring-offset-3"
                    >
                      <Bars3Icon className="size-5" aria-hidden="true" />
                      <span className="sr-only">
                        {data.settings.customTextTranslations.verticalAlignMiddleTranslation}
                      </span>
                    </Radio>
                    <Radio
                      value="bottom"
                      aria-label={
                        data.settings.customTextTranslations.verticalAlignBottomTranslation
                      }
                      className="ring-secondary data-checked:bg-secondary relative flex cursor-pointer items-center justify-center rounded p-0.5 focus:outline-hidden data-checked:text-white data-checked:ring-2 data-focus:data-checked:ring-3 data-focus:data-checked:ring-offset-3"
                    >
                      <BarsArrowDownIcon className="size-5" aria-hidden="true" />
                      <span className="sr-only">
                        {data.settings.customTextTranslations.verticalAlignBottomTranslation}
                      </span>
                    </Radio>
                  </RadioGroup>
                </div>
              </div>

              {/* Delete line */}
              {textLines.length > 1 && (
                <Button
                  onClick={() => {
                    const newTextLines = textLines.filter((_, index) => index !== i);
                    setTextLines(newTextLines);
                  }}
                  type="button"
                  className="ml-3 flex cursor-pointer items-center justify-self-stretch rounded-md bg-red-100 p-3 text-gray-700 shadow-sm duration-100 hover:bg-red-400 hover:text-white hover:shadow-md"
                >
                  <TrashIcon className="size-5" aria-hidden="true" />
                  <span className="sr-only">
                    {data.settings.customTextTranslations.deleteLineTranslation}
                  </span>
                </Button>
              )}
            </div>
          ))}
          {/* Add line */}
          <Button
            onClick={() => {
              setTextLines([
                ...textLines,
                { ...emptyLine(data.settings.customTextTranslations.defaultTextTranslation) }
              ]);
            }}
            type="button"
            className="bg-secondary hover:bg-secondary/80 flex cursor-pointer items-center gap-2 rounded-md border border-transparent px-2 py-0.5 font-medium text-white"
          >
            <PlusIcon className="size-5" />
            {data.settings.customTextTranslations.addLineTranslation}
          </Button>
        </div>

        <div className="md:grid-rows-auto block md:grid md:grid-cols-2 md:gap-12">
          <div>
            {/* Main dimension */}
            <div className="mt-8">
              <h2 className="text-sm font-medium text-gray-900">
                {data.settings.customTextTranslations.mainDimensionTranslation}
              </h2>

              <RadioGroup
                value={selectedDimension}
                onChange={setSelectedDimension}
                className="mt-2 flex flex-wrap items-center gap-3"
              >
                <Radio
                  value="width"
                  aria-label={data.settings.customTextTranslations.widthLabelTranslation}
                  className="ring-secondary border-secondary data-checked:bg-secondary relative flex cursor-pointer items-center justify-center rounded border px-2 py-0.5 focus:outline-hidden data-checked:text-white data-checked:ring-2 data-focus:data-checked:ring-3 data-focus:data-checked:ring-offset-3"
                >
                  {data.settings.customTextTranslations.widthLabelTranslation}
                </Radio>
                <Radio
                  value="height"
                  aria-label={data.settings.customTextTranslations.heightLabelTranslation}
                  className="ring-secondary border-secondary data-checked:bg-secondary relative flex cursor-pointer items-center justify-center rounded border px-2 py-0.5 focus:outline-hidden data-checked:text-white data-checked:ring-2 data-focus:data-checked:ring-3 data-focus:data-checked:ring-offset-3"
                >
                  {data.settings.customTextTranslations.heightLabelTranslation}
                </Radio>
              </RadioGroup>
            </div>
            {/* Sticker height */}
            <div className={twMerge('mt-3', selectedDimension === 'width' ? 'hidden' : '')}>
              <label htmlFor="height" className="block text-sm/6 font-medium text-gray-900">
                {data.settings.customTextTranslations.heightLabelTranslation} (cm)
              </label>
              <div className="mt-2">
                <input
                  id="height"
                  name="height"
                  type="number"
                  step={1}
                  min={selectedDimension === 'height' ? 10 : undefined}
                  max={selectedDimension === 'height' ? 120 : undefined}
                  defaultValue={stickerHeight}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value)) {
                      setStickerHeight(value);
                    } else {
                      setStickerHeight(10);
                    }
                  }}
                  className="focus:outline-secondary block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
                />
              </div>
            </div>
            {/* Sticker width */}
            <div className={twMerge('mt-3', selectedDimension === 'height' ? 'hidden' : '')}>
              <label htmlFor="width" className="block text-sm/6 font-medium text-gray-900">
                {data.settings.customTextTranslations.widthLabelTranslation} (cm)
              </label>
              <div className="mt-2">
                <input
                  id="width"
                  name="width"
                  type="number"
                  step={1}
                  min={selectedDimension === 'width' ? 10 : undefined}
                  max={selectedDimension === 'width' ? 120 : undefined}
                  defaultValue={stickerWidth}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value)) {
                      setStickerWidth(value);
                    } else {
                      setStickerWidth(10);
                    }
                  }}
                  className="focus:outline-secondary block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
                />
              </div>
            </div>

            {/* Orientation picker */}
            <div className="mt-8 flex items-center gap-6">
              <h2 className="text-sm font-medium text-gray-900">
                {data.settings.productPageTranslations.invertLabelTranslation}
              </h2>
              <fieldset aria-label={data.settings.productPageTranslations.invertLabelTranslation}>
                <Switch
                  checked={orientationInverted}
                  onChange={setOrientationInverted}
                  className="group data-[checked]:bg-secondary bg-secondary/20 data-focus:ring-secondary/80 inline-flex h-6 w-11 items-center rounded-full transition focus:outline-hidden data-focus:ring-2 data-focus:ring-offset-2"
                >
                  <span className="size-4 translate-x-1 rounded-full bg-white transition group-data-[checked]:translate-x-6" />
                </Switch>
              </fieldset>
            </div>
          </div>
          {/* Color picker */}
          <div>
            <h2 className="text-sm font-medium text-gray-900">
              {data.settings.productPageTranslations.colorLabelTranslation}
              {selectedColor && (
                <>
                  <span className="px-1">:</span>
                  <span className="font-medium">{selectedColor?.title}</span>
                </>
              )}
            </h2>

            <fieldset
              aria-label={data.settings.productPageTranslations.colorLabelTranslation}
              className="mt-2"
            >
              <RadioGroup
                value={selectedColor}
                onChange={setSelectedColor}
                className="flex flex-wrap items-center gap-3"
              >
                {data.colors.colorCategories?.map((colorCategory, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-3">
                    {colorCategory.colors?.map((color, j) => (
                      <Radio
                        key={j}
                        value={color}
                        aria-label={color.title}
                        style={{ backgroundColor: color.colorCode }}
                        aria-checked={selectedColor === color}
                        className="ring-secondary relative -m-0.5 flex cursor-pointer items-center justify-center rounded-full border-[1px] border-gray-300 p-0.5 focus:outline-hidden data-checked:ring-2 data-focus:data-checked:ring-3 data-focus:data-checked:ring-offset-3"
                      >
                        <span
                          aria-hidden="true"
                          style={{ borderColor: color.colorCode }}
                          className={'size-8 rounded-full border border-black/10'}
                        />
                        <span className="sr-only">{color.title}</span>
                      </Radio>
                    ))}
                  </div>
                ))}
              </RadioGroup>
            </fieldset>
          </div>
        </div>

        {/* Add to cart button */}
        <div className="sticky bottom-0 mx-auto mt-8 max-w-96 bg-gradient-to-t from-white to-transparent pt-2 pb-6 before:absolute before:inset-y-0 before:-left-8 before:w-8 before:bg-gradient-to-tl before:from-white before:via-transparent before:to-transparent after:absolute after:inset-y-0 after:-right-8 after:w-8 after:bg-gradient-to-tr after:from-white after:via-transparent after:to-transparent">
          <button
            type="submit"
            disabled={addItemPending}
            className="bg-secondary hover:text-secondary hover:bg-primary focus:ring-primary flex w-full cursor-pointer items-center justify-center rounded-md border border-transparent px-8 py-3 text-base font-medium text-white duration-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden"
          >
            <span className="font-semibold">
              {addItemPending
                ? data.settings.productPageTranslations.loadingTranslation
                : data.settings.productPageTranslations.addToCartLabelTranslation}
            </span>
            {selectedVariant?.price && (
              <span className="ml-1 opacity-80">
                ({formatPrice(selectedVariant?.price, locale)})
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
